import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
    selector: 'app-items-table',
    templateUrl: './items-table.component.html',
    styleUrls: ['./items-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ItemsTableComponent implements OnInit, OnDestroy {

  /** Subset of indexes to render. If omitted, all controls are rendered. */
  @Input() itemIndexes?: number[];
  @Input() orderItemsFormCustomDate: FormArray;
  @Input() showStockQty: boolean;
  @Input() supportsSplitDelivery: boolean = true;
  @Input() maxStoreCustomDate: string;
  @Input() componentId: string;
  /** Prefix used in checkbox id attributes to guarantee uniqueness across groups, e.g. 'orig-', 'cust-', 'style-' */
  @Input() idPrefix: string = '';
  /** When emitted, expands/collapses all item panels (used by accordion-list; grouped views omit this) */
  @Input() expandCollapse$?: Subject<'expand' | 'collapse'>;
  /** 'full' = tutte le colonne (default); 'color-size' = no immagine, colonna item mostra solo color+size */
  @Input() itemDisplayMode: 'full' | 'color-size' = 'full';
  /** Se presente, il pannello espandibile mostra solo le righe customDates che matchano questa data (per CustomDate view) */
  @Input() filterCustomDate?: string;

  @Output() dateAdded = new EventEmitter<{ orderItemIndex: number; customDate?: string }>();
  @Output() stockDateAdded = new EventEmitter<number>();
  @Output() customDateCleared = new EventEmitter<number>();
  @Output() dateRemoved = new EventEmitter<{ orderItemIndex: number; customDateIndex: number }>();
  @Output() dateTimeChanged = new EventEmitter<{ ev: string; form: AbstractControl }>();

  orderItemAccordionOpened: boolean = false;

  expandedItems = new Set<number>();
  private collapsedByUser = new Set<number>();
  private sub = new Subscription();

  get effectiveItemIndexes(): number[] {
    return this.itemIndexes ?? this.orderItemsFormCustomDate.controls.map((_, i) => i);
  }

  ngOnInit() {
    if (this.expandCollapse$) {
      this.sub.add(this.expandCollapse$.subscribe(action => {
        if (action === 'expand') {
          this.effectiveItemIndexes.forEach(i => this.expandedItems.add(i));
        } else {
          this.expandedItems.clear();
        }
      }));
    }
    this.sub.add(this.orderItemsFormCustomDate.valueChanges.pipe(debounceTime(150)).subscribe(() => {
      this.orderItemsFormCustomDate.controls.forEach((ctrl, index) => {
        if (!(ctrl.get('customDates').value as any[]).length) {
          this.expandedItems.delete(index);
        }
      });
    }));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  getItemForm(itemIndex: number): FormGroup {
    return this.orderItemsFormCustomDate.controls[itemIndex] as FormGroup;
  }

  getCustomDates(itemIndex: number): FormArray<FormGroup> {
    return this.getItemForm(itemIndex).get('customDates') as FormArray<FormGroup>;
  }

  hasCustomDates(itemIndex: number): boolean {
    return this.getEffectiveCustomDates(itemIndex).length > 0;
  }

  isExpanded(itemIndex: number): boolean {
    return this.expandedItems.has(itemIndex);
  }

  onAddDate(itemIndex: number): void {
    this.dateAdded.emit({ orderItemIndex: itemIndex });
    this.expandedItems.add(itemIndex);
    // Reset del flag di chiusura manuale: riaprendo l'accordion si annulla il close precedente
    this.collapsedByUser.delete(itemIndex);
  }

  onSingleDateChanged(itemIndex: number, customDate: string): void {
    this.dateAdded.emit({ orderItemIndex: itemIndex, customDate });
  }

  /** Restituisce il range {minDate, maxDate} delle custom date effettive di un item,
   *  ordinando per data ISO — usato per il display della cella "custom date". */
  getCustomDateRange(itemIndex: number): { minDate: string; maxDate: string } | null {
    const customDates = this.getEffectiveCustomDates(itemIndex);
    if (customDates.length < 2) { return null; }
    const sorted = customDates.map(d => d.date).filter(Boolean).sort();
    return { minDate: sorted[0], maxDate: sorted[sorted.length - 1] };
  }

  getMinCustomDate(itemIndex: number): string {
    const requestedDeliveryDate = this.getItemForm(itemIndex).get('requestedDeliveryDate').value as string;
    if (!requestedDeliveryDate) { return requestedDeliveryDate; }
    return this.addOneDay(requestedDeliveryDate);
  }

  private addOneDay(dateStr: string): string {
    if (!dateStr) { return dateStr; }
    const [yearStr, monthStr, dayStr] = dateStr.split('T')[0].split('-');
    const nextDateUtc = new Date(Date.UTC(Number(yearStr), Number(monthStr) - 1, Number(dayStr) + 1));
    return nextDateUtc.toISOString();
  }

  onClearSingleCustomDate(itemIndex: number): void {
    this.customDateCleared.emit(itemIndex);
  }

  getEffectiveCustomDates(itemIndex: number): Array<{ date: string; qty: number }> {
    const itemForm = this.getItemForm(itemIndex);
    const requestedDeliveryDate = itemForm.get('requestedDeliveryDate').value as string;
    const customDates = (itemForm.get('customDates').value as Array<{ date: string; qty: number }>) ?? [];
    if (this.supportsSplitDelivery && customDates.length > 1) {
      return customDates;
    }
    return customDates.filter(customDate => !this.isSameCalendarDate(customDate.date, requestedDeliveryDate));
  }

  /** Apertura manuale dell'accordion (senza emettere dateAdded) */
  openAccordion(itemIndex: number): void {
    this.expandedItems.add(itemIndex);
    this.collapsedByUser.delete(itemIndex);
  }

  /** Ritorna true se la riga di custom date deve essere visibile nel pannello.
   * Se `filterCustomDate` è impostato (siamo in CustomDate view), mostra solo le righe
   * che matchano quella data per calendario. Altrimenti mostra tutto. */
  isCustomDateVisible(control: AbstractControl): boolean {
    if (!this.filterCustomDate) { return true; }
    return this.isSameCalendarDate(control.get('date').value, this.filterCustomDate);
  }

  /** Chiusura manuale dell'accordion tramite il pulsante X */
  closeAccordion(itemIndex: number): void {
    this.expandedItems.delete(itemIndex);
    // Segna l'item come chiuso dall'utente per sovrascrivere l'auto-expand di hasCustomDates
    this.collapsedByUser.add(itemIndex);
  }

  trackByValue(index: number, value: number): number {
    return value;
  }

  private isSameCalendarDate(firstDate: string, secondDate: string): boolean {
    return firstDate?.split('T')[0] === secondDate?.split('T')[0];
  }
}
